export type HazardKind =
  | "rain"
  | "embers"
  | "sand"
  | "aurora"
  | "ice"
  | "void"
  | "lightning"
  | "singularity";

export interface StageTheme {
  skyTop: string;
  skyBottom: string;
  accent: string;
  accentSoft: string;
  danger: string;
  haze: string;
}

export interface StageConfig {
  id: number;
  roman: string;
  name: string;
  domain: string;
  subtitle: string;
  description: string;
  hazard: string;
  hazardKind: HazardKind;
  boss: string;
  bossTitle: string;
  duration: number;
  theme: StageTheme;
}

export interface HazardRule {
  title: string;
  caption: string;
  warning: number;
  active: number;
  cooldown: readonly [number, number];
}

export const HAZARD_RULES: Record<HazardKind, HazardRule> = {
  rain: {
    title: "横风锋面接近",
    caption: "CROSSWIND / HOLD YOUR LINE",
    warning: 0.75,
    active: 1.8,
    cooldown: [4.2, 5.1],
  },
  embers: {
    title: "等离子坠火锁定",
    caption: "CLEAR THE MARKED CORRIDOR",
    warning: 0.9,
    active: 1.25,
    cooldown: [4.5, 5.4],
  },
  sand: {
    title: "沙幕信号突增",
    caption: "AMBUSH FORMATION INBOUND",
    warning: 0.78,
    active: 0.9,
    cooldown: [5, 5.9],
  },
  aurora: {
    title: "磁极方向翻转",
    caption: "HOSTILE FIRE WILL CURVE",
    warning: 0.82,
    active: 2.25,
    cooldown: [4.7, 5.6],
  },
  ice: {
    title: "低温脉冲扩散",
    caption: "THRUST OUTPUT REDUCED",
    warning: 0.85,
    active: 1.5,
    cooldown: [4.6, 5.4],
  },
  void: {
    title: "局部引力井成形",
    caption: "BREAK AWAY FROM THE CORE",
    warning: 0.95,
    active: 2.35,
    cooldown: [5, 6],
  },
  lightning: {
    title: "雷柱航道标定",
    caption: "VACATE BOTH MARKED LANES",
    warning: 0.9,
    active: 0.34,
    cooldown: [4.1, 4.9],
  },
  singularity: {
    title: "奇点潮汐反转",
    caption: "PULL / REPULSE CYCLE",
    warning: 0.92,
    active: 2.55,
    cooldown: [4.8, 5.6],
  },
};

export const STAGES: readonly StageConfig[] = [
  {
    id: 1,
    roman: "I",
    name: "雨蚀穹顶",
    domain: "DOMAIN 01 // 暴雨层",
    subtitle: "RAIN-CROWNED SKY",
    description: "低空防线被不间断的酸雨切断。沿残存导航灯穿越积雨云，在雷达失效前击沉玄潮母舰。",
    hazard: "暴雨折射",
    hazardKind: "rain",
    boss: "玄潮母舰",
    bossTitle: "RAINMAKER CARRIER",
    duration: 30,
    theme: {
      skyTop: "#030a18",
      skyBottom: "#0a2340",
      accent: "#55d9ff",
      accentSoft: "#245b77",
      danger: "#8a86ff",
      haze: "#2f89b5",
    },
  },
  {
    id: 2,
    roman: "II",
    name: "赤烬回廊",
    domain: "DOMAIN 02 // 燃烧带",
    subtitle: "EMBER CORRIDOR",
    description: "轨道碎片在大气层里持续燃烧。避开坠落火线，摧毁将热流武器化的赤枭熔炉。",
    hazard: "烬雨灼流",
    hazardKind: "embers",
    boss: "赤枭熔炉",
    bossTitle: "CINDER FORGE",
    duration: 32,
    theme: {
      skyTop: "#050817",
      skyBottom: "#0b1b3b",
      accent: "#4e9bff",
      accentSoft: "#244b89",
      danger: "#a88bff",
      haze: "#315fba",
    },
  },
  {
    id: 3,
    roman: "III",
    name: "金砂盲区",
    domain: "DOMAIN 03 // 沙海层",
    subtitle: "GILDED BLINDZONE",
    description: "电离尘暴吞没了一切远距观测。敌机会从金砂幕墙中突然切入，守关者正潜伏在风暴眼。",
    hazard: "沙幕伏击",
    hazardKind: "sand",
    boss: "蜃骨潜航者",
    bossTitle: "MIRAGE DREADNOUGHT",
    duration: 34,
    theme: {
      skyTop: "#08091a",
      skyBottom: "#171945",
      accent: "#8a86ff",
      accentSoft: "#4c468e",
      danger: "#55d9ff",
      haze: "#675fc4",
    },
  },
  {
    id: 4,
    roman: "IV",
    name: "极光裂隙",
    domain: "DOMAIN 04 // 磁暴层",
    subtitle: "AURORA RIFT",
    description: "断裂的极光正在撕扯导航系统。跟随磁力脉冲的空隙推进，终止苍鸢阵列的锁定。",
    hazard: "磁轨偏折",
    hazardKind: "aurora",
    boss: "苍鸢阵列",
    bossTitle: "AURORA LANCER",
    duration: 36,
    theme: {
      skyTop: "#031019",
      skyBottom: "#082b35",
      accent: "#66e8cf",
      accentSoft: "#277f73",
      danger: "#74b5ff",
      haze: "#2e8e98",
    },
  },
  {
    id: 5,
    roman: "V",
    name: "霜环禁港",
    domain: "DOMAIN 05 // 冻结港",
    subtitle: "FROST-RING HARBOR",
    description: "废弃空港被绝对低温锁死，冻结的战舰却重新点火。击破冰层装甲，别让自己停在弹幕里。",
    hazard: "低温凝滞",
    hazardKind: "ice",
    boss: "白棘方舟",
    bossTitle: "PALETHORN ARK",
    duration: 38,
    theme: {
      skyTop: "#06111f",
      skyBottom: "#10294a",
      accent: "#b9f4ff",
      accentSoft: "#4f7995",
      danger: "#8a86ff",
      haze: "#6aa6c6",
    },
  },
  {
    id: 6,
    roman: "VI",
    name: "无光深井",
    domain: "DOMAIN 06 // 深空井",
    subtitle: "LIGHTLESS WELL",
    description: "这里没有地平线，只有被引力拉长的星光。守关者会扭曲弹道，保持移动就是唯一坐标。",
    hazard: "引力扭曲",
    hazardKind: "void",
    boss: "噬昼者",
    bossTitle: "DAY-EATER",
    duration: 40,
    theme: {
      skyTop: "#02030c",
      skyBottom: "#0e0a2a",
      accent: "#a88bff",
      accentSoft: "#51418c",
      danger: "#55d9ff",
      haze: "#504c9f",
    },
  },
  {
    id: 7,
    roman: "VII",
    name: "万雷天阶",
    domain: "DOMAIN 07 // 雷暴核",
    subtitle: "TEN-THOUSAND THUNDERS",
    description: "所有风暴在此汇流。高压雷柱会切割安全区，必须在下一次闪击前撕开天阶防线。",
    hazard: "雷柱封锁",
    hazardKind: "lightning",
    boss: "九霄雷兽",
    bossTitle: "SKYBREAK BEAST",
    duration: 42,
    theme: {
      skyTop: "#040817",
      skyBottom: "#111c3a",
      accent: "#74b5ff",
      accentSoft: "#355d92",
      danger: "#d1c4ff",
      haze: "#526da8",
    },
  },
  {
    id: 8,
    roman: "VIII",
    name: "终焉星门",
    domain: "DOMAIN 08 // 奇点门",
    subtitle: "TERMINUS GATE",
    description: "八域风暴背后的控制核心已经显形。穿过坍缩星门，终止那枚持续改写天空的零号协议。",
    hazard: "奇点潮汐",
    hazardKind: "singularity",
    boss: "零号天枢",
    bossTitle: "AXIS ZERO",
    duration: 45,
    theme: {
      skyTop: "#050517",
      skyBottom: "#171044",
      accent: "#d1c4ff",
      accentSoft: "#6955a6",
      danger: "#66e8cf",
      haze: "#6c62b8",
    },
  },
] as const;

export const MAX_WEAPON_LEVEL = 8;
export const MAX_WING_LEVEL = 3;
export const PLAYER_MAX_HP = 72;
export const PLAYER_REPAIR_AMOUNT = 18;
export const GUARANTEED_PICKUP_INTERVAL = 14;
export const BASE_PICKUP_DROP_CHANCE = 0.025;
export const TANK_PICKUP_DROP_BONUS = 0.12;
export const TURRET_PICKUP_DROP_BONUS = 0.045;
export const MAX_ACTIVE_PICKUPS = 2;
export const BOSS_HP_BY_STAGE = [1450, 1970, 2490, 3010, 3530, 4050, 5200, 11000] as const;
export const GAME_WIDTH = 480;
export const GAME_HEIGHT = 800;

export function stageByIndex(index: number): StageConfig {
  return STAGES[Math.max(0, Math.min(STAGES.length - 1, index))];
}
